import {
	Component,
	Input,
	ViewEncapsulation,
	ViewChild,
	ElementRef,
	Output,
	EventEmitter,
	OnDestroy,
	ChangeDetectorRef,
	ChangeDetectionStrategy
} from "@angular/core";
import { IdService } from "../shared/services/id.service";
import { PopoverService } from "../shared/services/popover.service";
import { trigger, style, animate, transition, state } from "@angular/animations";

@Component({
	selector: "rbc-menu",
	template: `
		<div
			#self
			[id]="id"
			[ngClass]="getWrapperClass()"
			class="rbc-menu"
			(click)="toggle($event)"
			(mouseleave)="hoverable ? onMouseOut($event): null"
			(mouseenter)="hoverable ? onMouseOver($event) : null"
		>
			<!-- Slot for menu trigger -->
			<div #triggerSlot>
				<ng-content select="[trigger]"> </ng-content>
			</div>

			<div
				[id]="'menuTriggerSlot' + id"
				*ngIf="!triggerSlot.children.length">
				<button
					#button
					[id]="'menuTrigger' + id"
					[attr.aria-controls]="'menuContent'+id"
					[attr.aria-label]= "labelTrigger"
					[attr.aria-expanded]= "isOpen ? true : false"
					[attr.aria-haspopup]= "true"
					class= "rbc-menu-trigger-icon"
					(click)="onButtonClick.emit($event)">
					<rbc-icon
						[type]= "iconWeight"
						[icon]= "icon ? icon : 'ellipsis-v'"
						[attr.aria-hidden]= "true"></rbc-icon>
				</button>
			</div>
			<ul
				#content
				[hidden]="!isOpen"
				[@opacity]="{value: isOpen, params:{topVar: topGen}}"
				(@opacity.start)="animationDone($event)"
				[id]="'menuContent' + id"
				[attr.aria-labelledby]="'menuTrigger' + id"
				[ngClass]="classGen()"
				class="rbc-menu-content"
				[ngStyle]="{
					'top': topGen() + 'px',
					'left': (overflowLeft() || leftGen()) + 'px'
				}"
				role="menu"
				tabindex= "-1">
				<ng-content></ng-content>
			</ul>
		</div>

	`,
	styles: [],
	animations: [
		trigger("opacity", [
			state("true", style({
				opacity: 1
			})),
			state("false", style({
				opacity: 0
			})),
			state("void", style({
				opacity: 0

			})),
			transition("* => false", [style({
				opacity: 0
			}), animate("250ms ease-in")]),
			transition("* => true", animate("250ms ease-out"))
		])
	],
	providers: [PopoverService],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuComponent implements OnDestroy {
	get isOpen(): boolean {
		// console.log(this.popoverService.isOpen || this.active)
		return  this.active;
	}
	set isOpen(change: boolean) {
		this.active = change;
	}
	topGen(): number {
		if(this.isOpen){
			return this.popoverService.topGen();
		}
		return
	}
	leftGen(): number {
		if(this.isOpen){
			return this.popoverService.leftGen();
		}
		return
	}
	overflowLeft(): number {
		if(this.isOpen){
			return this.popoverService.overflowLeft();
		}
		return
	}

	@Input() active = false;
	@Input() clickable = true;
	@Input() hoverable  = false;
	@Input() icon: string;
	@Input() iconWeight: "solid" | "light" = "light";
	@Input() id: string;
	@Input() labelTrigger = "Toggle menu";
	@Input() position: "top" | "right" | "bottom" | "left" = "bottom";
	@Input() dropdownPositionX: "left" | "right" | "center" = "center";
	@Input() absolute;
	@Input() wrapperClass;

	@Output() popoverToggle = new EventEmitter<boolean>();
	@Output() onButtonClick = new EventEmitter();

	@ViewChild("self", { static: false }) self: ElementRef;
	@ViewChild("content", { static: false }) content: ElementRef;
	@ViewChild("button", { static: false }) button: ElementRef;
	@ViewChild("triggerSlot", { static: false }) triggerSlot: ElementRef;
	@ViewChild("contentSlot", { static: false }) contentSlot: ElementRef;

	clickListener = this.$_Popover_toggleClose.bind(this);


	constructor(
		private idService: IdService,
		private popoverService: PopoverService,
		private cdr: ChangeDetectorRef,
		private eRef: ElementRef
	) {
		this.id = idService.generate();
	}

	onMouseOver(event: Event) {
		event.preventDefault();
		this.hoverToggle(true)
	}

	onMouseOut(event: Event) {
		event.preventDefault();
		this.hoverToggle(false)
	}

	animationDone(e) {
		if (e.toState === true && this.content) {
			if (this.content && this.self) {
				this.popoverService.syncProps({
					clickable: this.clickable,
					active: this.active,
					hover: this.hoverable ,
					position: this.position,
					positionX: this.dropdownPositionX,
					offset: 10,
					triggerRef: (this.self.nativeElement as HTMLElement),
					popoverRef: this.content.nativeElement,
					absolute: this.absolute,
					pageOffset: window.pageYOffset
				});
				this.popoverService.enter(this.content.nativeElement);
			}
		}
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
		// ensure menu is fully removed
		this.isOpen = false;
	}


	// Toggle the menu and emit an event of its current visibility
	toggle(e) {
		const content = this.content.nativeElement;
		if (this.clickable && !(content === e.target || (content && content.contains(e.target)))) {
			this.isOpen = !this.isOpen;
			this.popoverToggle.emit(this.isOpen);

			// allow tooltip to be closed on document click / esc key press
			if (this.isOpen) {
				['click', 'touchend', 'keydown'].forEach(handler => document.addEventListener(handler, this.clickListener));
			} else {
				['click', 'touchend', 'keydown'].forEach(handler => document.removeEventListener(handler, this.clickListener));
			}
		}
	}

	hoverToggle(toggle) {
		if (this.hoverable) {
			this.isOpen = toggle;
			this.popoverToggle.emit(toggle);
		}
	}

	$_Popover_toggleClose(e) {
		const el = this.self.nativeElement;
		const content = this.content.nativeElement;
		// when tabbing outside of the content, return focus to the trigger
		// and close the popover
		if ((e.key && e.key.toLowerCase().indexOf('tab') != -1)) {
			setTimeout(() => {
				if (!content.contains(document.activeElement)) {
					this.$_Popover_close();
				}
			});
		}

		if (
			(e.key && e.key.toLowerCase().indexOf('esc') != -1) ||
			(!(el === e.target || el.contains(e.target)) &&
			(content && !(content === e.target || content.contains(e.target))))
		) {
			this.$_Popover_close();
		}
	}
	$_Popover_close() {
		this.isOpen = false;
		this.cdr.markForCheck();
		// if (this.absolute && this.button) {
		// 	this.button.nativeElement.focus();
		// }
		['click', 'touchend', 'keydown'].forEach(handler => document.removeEventListener(handler, this.clickListener));
	}

	initiatePopover(event) {
		if (event.type === "enter") {
			this.popoverService.enter(this.content.nativeElement);
		}
	}

	classGen() {
		const classes = [];
		if (this.position) {
			classes.push(`rbc-menu-${this.position}`);
		}
		if (this.overflowLeft) {
			classes.push("rbc-menu-overflow");
		}
		if (this.dropdownPositionX === "left") {
			classes.push("rbc-menu-flush-left");
		}
		if (this.dropdownPositionX === "right") {
			classes.push("rbc-menu-flush-right");
		}
		return classes;
	}

	getWrapperClass() {
		let classes = {};
		classes["open"] = this.isOpen;
		classes["rbc-menu-relative"] = !this.absolute;
		classes[this.wrapperClass] = this.wrapperClass;
		return classes;
	}
}
